<template>
  <div class="container mx-auto p-6">
    <!-- Título -->
    <h1 class="text-3xl font-bold mb-4 text-center">
      Atribuir Questões de Segurança ao {{ NomeFuncionario }}
    </h1>
    <h2 class="text-xl font-bold">{{ NomeDaEmpresa }}</h2>

    <!-- Alerta se nenhum funcionário for selecionado -->
    <div
      v-if="!FuncionarioID"
      class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4"
    >
      <p>
        Nenhum funcionário selecionado. Selecione um funcionário para atribuir
        questões.
      </p>
    </div>

    <!-- Barra de busca -->
    <div class="mb-4 flex items-center space-x-4">
      <input
        type="text"
        v-model="searchQuery"
        class="p-2 border border-gray-300 rounded"
        placeholder="Pesquisar (ID ou Descrição)..."
      />
      <button
        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        @click="loadControles"
      >
        Buscar
      </button>
    </div>

    <!-- Tabelas -->
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Controles disponíveis -->
      <div
        class="flex-1 border border-gray-200 rounded-lg overflow-hidden shadow"
      >
        <div class="bg-gray-100 p-3 flex justify-between items-center">
          <h2 class="text-xl font-bold">Controles Disponíveis</h2>
          <span class="text-gray-600 bg-white px-2 py-1 rounded">
            {{ filteredQuestoes.length }} itens
          </span>
        </div>
        <div class="overflow-auto" style="max-height: 500px">
          <table class="min-w-full table-auto border-collapse text-black">
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th class="border px-4 py-2">ID</th>
                <th class="border px-4 py-2">Nome do Controle</th>
                <th class="border px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="questao in filteredQuestoes"
                :key="questao._id"
                :class="{
                  'bg-blue-50': questao.associado,
                  'hover:bg-gray-50': !questao.associado,
                }"
              >
                <td class="border px-4 py-2">{{ questao.ControllerId }}</td>
                <td class="border px-4 py-2">
                  {{ questao.ControllerName }}
                  <span
                    v-if="questao.associado"
                    class="ml-2 text-green-600 text-sm"
                    >(Já associado)</span
                  >
                </td>
                <td class="border px-4 py-2">
                  <!-- Exibe o botão somente se a questão ainda não estiver associada -->
                  <button
                    v-if="!questao.associado"
                    class="px-3 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
                    @click="
                      atribuirtQuestoesDoControle(
                        questao._id,
                        questao.ControllerName
                      )
                    "
                    :disabled="!FuncionarioID || isLoading"
                  >
                    Associar
                  </button>

                  <!-- Ou uma indicação visual, se já estiver associado -->
                  <span v-else class="text-green-600 text-sm"
                    >Já associado</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="filteredQuestoes.length === 0"
            class="p-4 text-center text-gray-500"
          >
            Nenhuma questão disponível.
          </div>
        </div>
      </div>

      <!-- Questões atribuídas -->
      <div
        class="flex-1 border border-gray-200 rounded-lg overflow-hidden shadow"
      >
        <div class="bg-gray-100 p-3 flex justify-between items-center">
          <h2 class="text-xl font-bold">Questões de {{ NomeFuncionario }}</h2>

          <span class="text-gray-600 bg-white px-2 py-1 rounded">
            {{ questoesFuncionario.length }} itens
          </span>
        </div>
        <div class="overflow-auto" style="max-height: 500px">
          <table
            v-if="questoesFuncionario.length > 0"
            class="min-w-full table-auto border-collapse text-black"
          >
            <!-- Correção nas colunas da tabela de questões atribuídas -->
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th class="border px-4 py-2">Descrição</th>
                <th class="border px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="questao in questoesFuncionario"
                :key="questao._id"
                class="hover:bg-gray-50 cursor-pointer"
                @click="mostrarQuestoesDoControle(questao.ControllerID)"
              >
                <td class="border px-4 py-2">{{ questao.Option }}</td>
                <td class="border px-4 py-2">
                  <button
                    class="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    @click.stop="removerQuestao(questao)"
                    :disabled="isLoading"
                  >
                    Remover
                  </button>
                </td>
              </tr>
              <!-- Lista as questões relacionadas abaixo -->
              <tr v-if="questoesSelecionadas.length > 0">
                <td colspan="4">
                  <div class="bg-gray-100 p-4 rounded mt-2">
                    <h3 class="font-bold mb-2">
                      Questões do Controller Selecionado:
                    </h3>
                    <ul class="list-disc ml-6">
                      <li v-for="q in questoesSelecionadas" :key="q._id">
                        {{ q.texto }}
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
              ""
            </tbody>
          </table>
          <div v-else class="p-4 text-center text-gray-500">
            Nenhuma questão atribuída a este funcionário.
          </div>
        </div>
      </div>
    </div>
    <!-- Tabela com controles atribuidos -->
    <div class="flex flex-col lg:flex-row gap-6 mt-6">
      <div
        class="flex-1 border border-gray-200 rounded-lg overflow-hidden shadow"
      >
        <div class="bg-gray-100 p-3 flex justify-between items-center">
          <h2 class="text-xl font-bold">Controles Atribuídos</h2>
          <span class="text-gray-600 bg-white px-2 py-1 rounded">
            {{ controle.length }} itens
          </span>
        </div>
        <div class="overflow-auto" style="max-height: 500px">
          <table class="min-w-full table-auto border-collapse text-black">
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th class="border px-4 py-2">ID</th>
                <th class="border px-4 py-2">Nome do Controle</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="controle in controle" :key="controle._id">
                <td class="border px-4 py-2">{{ controle.ControllerId }}</td>
                <td class="border px-4 py-2">{{ controle.ControllerName }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <button
      class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mt-4 lg:mt-0"
      @click="refresh"
    >
      Atualizar
    </button>
    <!-- Notificação -->
    <div
      v-if="notification.show"
      :class="[
        'p-4 mb-4 rounded',
        notification.type === 'success'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700',
      ]"
    >
      {{ notification.message }}
      <button @click="notification.show = false" class="float-right font-bold">
        &times;
      </button>
    </div>
  </div>
  <div class="w-full md:w-1/2 p-4"></div>
</template>

<script>
import {
  assignQuestion,
  getQuestionByEmployeeId,
  getQuestionsByControllerId,
  verifyEmployeeQuestion,
} from '@/services/qa/api.question'

import {
  assignController,
  getControllersByEnterpriseId,
  getControllersInEmployees,
} from '@/services/security-controls/api.controllers'

export default {
  props: {
    FuncionarioID: String || '67ef0ebba58223a5caedeb45',
    NomeFuncionario: String || 'Nome do Funcionário',
    EmpresaID: String,
    NomeDaEmpresa: String,
    NomeDoControle: String,
  },
  data() {
    return {
      searchQuery: '',
      questoes: [],
      controle: [],
      questoesFuncionario: [],
      questoesSelecionadas: [],
      isLoading: false,
      notification: {
        show: false,
        message: '',
        type: 'success',
      },
    }
  },

  computed: {
    filteredQuestoes() {
      const query = this.searchQuery.toLowerCase()
      return this.questoes.filter(
        (q) =>
          !this.questoesFuncionario.some((f) => f._id === q._id) &&
          (q._id.toLowerCase().includes(query) ||
            q.Option.toLowerCase().includes(query))
      )
    },
  },
  methods: {
    async refresh() {
      this.isLoading = true
      await this.loadControles()
      await this.loadControlesFuncionario()
      await this.loadControlesByEmployees(this.FuncionarioID)
      await this.loadControlesByEmployees(this.FuncionarioID)
      this.isLoading = false
    },

    async loadControles() {
      try {
        this.isLoading = true
        const todosControles = await getControllersByEnterpriseId(
          this.EmpresaID || '67e577c96851a5f2b4f2c7b9'
        )
        if (todosControles && todosControles.length > 0) {
          this.questoes = todosControles
        } else {
          this.notificar('Nenhuma questão encontrada.', 'warning')
        }
      } catch (error) {
        this.notificar('Erro ao carregar questões.', 'error')
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async loadControlesByEmployees(id) {
      try {
        this.isLoading = true
        const controle = await getControllersInEmployees(id)
        if (controle) {
          this.controle = controle
          console.log('Controles atribuídos:', controle)
        } else {
          this.notificar('Nenhum controle encontrado.', 'warning')
        }
      } catch (error) {
        this.notificar(
          'Erro ao carregar controle. ',
          'Nenhum controle atribuído.',
          'error'
        )
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async loadControlesFuncionario() {
      try {
        this.isLoading = true

        const questoesAtribuidas = await getQuestionByEmployeeId(
          this.FuncionarioID
        )

        if (questoesAtribuidas && questoesAtribuidas.length > 0) {
          this.questoesFuncionario = questoesAtribuidas
        } else {
          this.notificar('Nenhuma questão atribuída encontrada.', 'warning')
        }
      } catch (error) {
        this.notificar('Nenhuma questão atribuída .')
      } finally {
        this.isLoading = false
      }
    },

    async getQuestionByControllerid(id) {
      try {
        this.isLoading = true
        const questoes = await getQuestionsByControllerId(id)

        if (questoes && questoes.length > 0) {
          this.questoes = questoes
        } else {
          this.notificar('Nenhuma questão encontrada.', 'warning')
        }
      } catch (error) {
        this.notificar('Erro ao carregar questões.', 'error')
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    async removerQuestao(questao) {
      try {
        this.isLoading = true
        await assignQuestion(questao._id, {
          EmployeesID: this.FuncionarioID,
          action: 'remove',
        })
        await assignController(questao.ControllerID, {
          EmployeesID: this.FuncionarioID,
          action: 'remove',
        })
        await this.loadControlesByEmployees(this.FuncionarioID)
        this.questoesFuncionario = this.questoesFuncionario.filter(
          (q) => q._id !== questao._id
        )
        this.notificar('Questão removida com sucesso!', 'success')
      } catch {
        this.notificar('Erro ao remover questão.', 'error')
      } finally {
        this.isLoading = false
      }
    },

    async atribuirtQuestoesDoControle(controllerId, controllerName) {
      try {
        this.isLoading = true

        const questoesDoControle =
          await getQuestionsByControllerId(controllerId)
        if (!questoesDoControle || questoesDoControle.length === 0) {
          this.notificar(
            `Nenhuma questão encontrada para o controle "${controllerName}".`,
            'warning'
          )
          return
        }

        for (const questao of questoesDoControle) {
          const exists = await verifyEmployeeQuestion(
            questao._id,
            this.FuncionarioID
          )

          if (!exists) {
            await assignQuestion(questao._id, {
              EmployeesID: this.FuncionarioID,
              action: 'add',
            })
            await assignController(questao.ControllerID, {
              EmployeesID: this.FuncionarioID,
              action: 'add',
            })
            await this.loadControlesByEmployees(this.FuncionarioID)
            console.log(`ID "${questao.ControllerID}" atribuída com sucesso!`)
          }
        }

        await this.loadControlesFuncionario()
        this.notificar(
          `Questões de "${controllerName}" atribuídas com sucesso!`,
          'success'
        )
      } catch (error) {
        this.notificar('Erro ao atribuir questões.', 'error')
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    async mostrarQuestoesDoControle(controllerId) {
      try {
        this.isLoading = true
        const questoes = await getQuestionsByControllerId(controllerId)

        if (questoes && questoes.length > 0) {
          this.questoesSelecionadas = questoes.map((q) => ({
            _id: q._id,
            texto: q.Option,
          }))
        } else {
          this.questoesSelecionadas = []
          this.notificar(
            'Nenhuma questão encontrada para este controle.',
            'info'
          )
        }
      } catch (error) {
        this.notificar('Erro ao carregar questões do controle.', 'error')
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    notificar(msg, tipo = 'success') {
      this.notification.message = msg
      this.notification.type = tipo
      this.notification.show = true

      setTimeout(() => {
        this.notification.show = false
      }, 4000)
    },
  },

  notificar(message, type = 'success') {
    this.notification = {
      show: true,
      message,
      type,
    }
  },

  watch: {
    FuncionarioID: {
      immediate: true,
      handler() {
        this.loadControles()
        this.loadControlesFuncionario()
        this.loadControlesByEmployees(this.FuncionarioID)
      },
    },
  },
}
</script>

<style scoped>
button:hover {
  transform: translateY(-1px);
  transition: transform 0.2s;
}
</style>
